import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import ImpersonationBanner from './components/ImpersonationBanner'
import AnnouncementsBanner from './components/AnnouncementsBanner'
import LegalAcceptanceModal from './components/LegalAcceptanceModal'
import Landing from './pages/Landing'
import Register from './pages/Register'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import NewDocument from './pages/NewDocument'
import DocumentEditor from './pages/DocumentEditor'
import DocumentStatus from './pages/DocumentStatus'
import SignerFlow from './pages/sign/SignerFlow'
import SignComplete from './pages/SignComplete'
import Verify from './pages/Verify'
import SelfieMobile from './pages/SelfieMobile'
import Privacy from './pages/Privacy'
import Dpa from './pages/Dpa'
import SignerTerms from './pages/SignerTerms'
import Help from './pages/Help'
import HelpArticle from './pages/HelpArticle'
import ReportAbuse from './pages/ReportAbuse'
import ExerciseRights from './pages/ExerciseRights'
import Status from './pages/Status'
import SubProcessors from './pages/SubProcessors'
import Pricing from './pages/Pricing'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import GlobalLanguageSwitcher from './components/GlobalLanguageSwitcher'
import Terms from './pages/Terms'
import GoogleAuthSuccess from './pages/GoogleAuthSuccess'
import ApiSettings from './pages/ApiSettings'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('pakt_token')
  if (!token) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  const { i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dir = i18n.dir()

  // Allow ?lang=xx to override language (for testing)
  useEffect(() => {
    const lang = searchParams.get('lang')
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang)
      const rtl = ['ar', 'ar-AE', 'he'].some(r => lang.startsWith(r.split('-')[0]))
      document.documentElement.dir = rtl ? 'rtl' : 'ltr'
      document.documentElement.lang = lang
    }
  }, [searchParams, i18n])

  // Captura ?impersonate=<jwt> emitido pelo painel admin. Salva o token
  // como sessão atual e leva pro dashboard. O banner aparece via JWT claim.
  useEffect(() => {
    const t = searchParams.get('impersonate')
    if (!t) return
    localStorage.setItem('pakt_token', t)
    // Tenta extrair o email do payload pra preencher pakt_user mínimo —
    // o dashboard usa esses campos pra UI até a primeira chamada à API.
    try {
      const parts = t.split('.')
      if (parts.length === 3) {
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const padded = b64 + '==='.slice((b64.length + 3) % 4)
        const payload = JSON.parse(atob(padded))
        localStorage.setItem('pakt_user', JSON.stringify({
          email: payload.email,
          nome: payload.email,
          countryCode: 'BR',
        }))
      }
    } catch { /* não-fatal */ }
    navigate('/dashboard', { replace: true })
  }, [searchParams, navigate])

  return (
    <div dir={dir} className="min-h-screen bg-gray-50">
      <ImpersonationBanner />
      <AnnouncementsBanner />
      <LegalAcceptanceModal />
      <GlobalLanguageSwitcher />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacidade" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/termos" element={<Terms />} />
        <Route path="/dpa" element={<Dpa />} />
        <Route path="/termos-signatario" element={<SignerTerms />} />
        <Route path="/ajuda" element={<Help />} />
        <Route path="/help" element={<Help />} />
        <Route path="/ajuda/:slug" element={<HelpArticle />} />
        <Route path="/help/:slug" element={<HelpArticle />} />
        <Route path="/report-abuse" element={<ReportAbuse />} />
        <Route path="/privacidade/exercer-direitos" element={<ExerciseRights />} />
        <Route path="/status" element={<Status />} />
        <Route path="/sub-processadores" element={<SubProcessors />} />
        <Route path="/subprocessors" element={<SubProcessors />} />
        <Route path="/precos" element={<Pricing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/documents/new" element={<ProtectedRoute><NewDocument /></ProtectedRoute>} />
        <Route path="/documents/:id/edit" element={<ProtectedRoute><DocumentEditor /></ProtectedRoute>} />
        <Route path="/documents/:id" element={<ProtectedRoute><DocumentStatus /></ProtectedRoute>} />
        <Route path="/selfie/:mobileToken" element={<SelfieMobile />} />
        <Route path="/sign/:token" element={<SignerFlow />} />
        <Route path="/sign/:token/complete" element={<SignComplete />} />
        <Route path="/verify/:code" element={<Verify />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
        <Route path="/settings/api" element={<ProtectedRoute><ApiSettings /></ProtectedRoute>} />
      </Routes>
    </div>
  )
}
